import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Thermometer, Snowflake, Gauge, Radio, PenLine } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import ZnamenkoTlacitko, { muzeZaporne } from '../ui/ZnamenkoTlacitko';
import BodEditor from './BodEditor';
import { SECTION_COLOR, SECTION_BG, TYP_BODU, Trend, cislo, kdy, hlavicky } from './ui';

/**
 * Kontroly a měření → Měřicí body. Norma §1: seznam seskupený podle typu
 * (lednice a mrazáky, měřidla, ostatní) s poslední hodnotou a mini-trendem ·
 * detail s grafem, historií a kde se bod měří · úprava v inspektoru (§1f),
 * zápis měření v modalu.
 *
 * Bod měří ručně (checklist, Zapsat měření), nebo bere hodnoty z kanálu uzlu
 * pobočky — interval a agregace jsou u bodu, detail je pro všechny stejný
 * (graf, historie, limity). Žádná veličina nemá vlastní obrazovku: proud
 * na fázi = další bod na kanálu fáze s limitem.
 * Data: /api/mereni/prehled (seznam), /api/mereni/bod/{id} (detail).
 */
const SKUPINY = [
    { id: 'chlad', nazev: 'Lednice a mrazáky', patri: b => ['lednice', 'mrazak'].includes(b.typ) },
    { id: 'meridla', nazev: 'Měřidla', patri: b => b.typ === 'meridlo' },
    { id: 'jine', nazev: 'Ostatní', patri: b => !['lednice', 'mrazak', 'meridlo'].includes(b.typ) },
];
const POHLEDY = [
    { id: 'vse', label: 'Aktivní', patri: () => true },
    { id: 'odchylka', label: 'Odchylka', upozorneni: true, patri: b => b.stav === 'odchylka' },
    { id: 'nezmereno', label: 'Nezměřeno', upozorneni: true, patri: b => b.stav === 'nezmereno' || b.stav === 'cidlo' },
];
const STAV = { ok: ['V pořádku', '#10B981'], odchylka: ['Odchylka', '#EF4444'], nezmereno: ['Nezměřeno', '#F59E0B'], cidlo: ['Čidlo neposílá', '#F59E0B'] };
const IKONA = { lednice: Thermometer, mrazak: Snowflake, meridlo: Gauge };
export const INTERVAL = { 1: '1 min', 5: '5 min', 10: '10 min', 15: '15 min', 30: '30 min', 60: 'hodinu', 1440: 'den' };
export const AGREGACE = { prumer: 'průměr', posledni: 'poslední hodnota', minimum: 'minimum', maximum: 'maximum' };
const CHOVANI = { rozmezi: 'drží se v rozmezí', rostouci: 'stále roste', klesajici: 'stále klesá', volna: 'volná' };
const limitText = (b) => b.limit_min != null && b.limit_max != null ? `${cislo(b.limit_min)} až ${cislo(b.limit_max)}` : b.limit_max != null ? `≤ ${cislo(b.limit_max)}` : b.limit_min != null ? `≥ ${cislo(b.limit_min)}` : null;

export default function MericiBody({ isDark, isMobile, cardStyle, inputStyle, mutedColor, upravuje, rezervaDole = null, otevritId = null }) {
    const [body, setBody] = useState(null);
    const [pohled, setPohled] = useState('vse');
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(otevritId ? Number(otevritId) : null);
    const [detail, setDetail] = useState(null);
    const [editor, setEditor] = useState(null);   // null | 'new' | parametr
    const [zapis, setZapis] = useState(null);     // modal Zapsat měření: { hodnota, kdy, vysledek }
    const [uzly, setUzly] = useState([]);         // uzly poboček (třetí sloupec)
    useEffect(() => { if (otevritId) setVybranyId(Number(otevritId)); }, [otevritId]);

    const nacti = useCallback(() => {
        fetch('/api/mereni/prehled?vse=1', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : null))
            .then(d => setBody(d?.body || [])).catch(() => setBody([]));
        fetch('/api/uzly', { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : []))
            .then(d => setUzly(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);
    useEffect(() => { nacti(); }, [nacti]);
    const nactiDetail = useCallback(() => {
        if (!vybranyId) return;
        fetch(`/api/mereni/bod/${vybranyId}`, { headers: { Accept: 'application/json' } }).then(r => (r.ok ? r.json() : null)).then(setDetail).catch(() => {});
    }, [vybranyId]);
    useEffect(() => { setDetail(null); nactiDetail(); }, [nactiDetail]);

    const ted = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
    const ulozZapis = async () => {
        const hodnota = parseFloat(String(zapis.hodnota).replace(',', '.'));
        if (Number.isNaN(hodnota)) return;
        const telo = { parametr_id: vybranyId, hodnota };
        if (detail?.muze_zapsat_zpetne && zapis.kdy && zapis.kdy !== zapis.puvodne) telo.namereno_at = new Date(zapis.kdy).toISOString();
        const r = await fetch('/api/mereni', { method: 'POST', headers: hlavicky(), body: JSON.stringify(telo) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setZapis(z => ({ ...z, vysledek: { chyba: d.message || d.error || 'Měření se nepodařilo uložit.' } })); return; }
        nacti(); nactiDetail();
        const m = d.mereni || {};
        if (m.stav === 'mimo' || m.podezrele) setZapis(z => ({ ...z, vysledek: { mereni: m } }));
        else setZapis(null);
    };

    // Horní hrana obsahu pro inspektor (norma §1f)
    const obsahRef = useRef(null);
    const [top, setTop] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = obsahRef.current; if (el) setTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [vybranyId, editor]);

    const vse = body || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, vse.filter(p.patri).length])), [vse]);
    const akt = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const seznam = vse.filter(b => akt.patri(b) && (!q || `${b.nazev} ${b.umisteni || ''}`.toLowerCase().includes(q)));
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    const radek = (b, siroky) => {
        const [st, stB] = STAV[b.stav] || STAV.ok;
        const Ikona = b.cidlo ? Radio : (IKONA[b.typ] || Gauge);
        const sel = b.id === vybranyId;
        const lt = limitText(b);
        return (
            <button key={b.id} type="button" onClick={() => setVybranyId(b.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, background: sel ? SECTION_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <Ikona size={16} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{b.nazev}</p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: muted }}>
                        {!siroky && b.stav !== 'ok' && <span style={{ color: stB, fontWeight: 700 }}>{st} · </span>}
                        {[b.umisteni, b.kanal && 'z uzlu', b.frekvence === 'tydne' ? 'týdně' : b.frekvence === 'denne' ? 'denně' : null, lt].filter(Boolean).join(' · ')}
                    </p>
                </div>
                {siroky && <Trend rada={b.rada} min={b.limit_min} max={b.limit_max} sirka={140} />}
                {siroky && <span style={{ width: 150, fontSize: 12, color: muted }}>{b.posledni ? `${kdy(b.posledni.kdy)}${b.posledni.kdo ? ` · ${b.posledni.kdo}` : ''}` : '—'}</span>}
                <span style={{ width: siroky ? 110 : 'auto', flexShrink: 0, textAlign: 'right', fontWeight: 700, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', color: b.stav === 'odchylka' ? '#EF4444' : 'var(--text-primary)' }}>
                    {b.posledni ? `${cislo(b.posledni.hodnota)} ${b.jednotka || ''}` : '—'}
                </span>
                {siroky && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, color: stB, background: `${stB}1f`, whiteSpace: 'nowrap' }}>{st}</span>}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat měřicí bod…', placeholderKratky: 'Hledat' }}
                akce={upravuje ? [{ id: 'novy', label: 'Nový bod', Icon: Plus, primarni: true, onClick: () => setEditor('new') }] : []}
                pohled={POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))} />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {body === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {body && seznam.length === 0 && <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>{q ? 'Nic se neshoduje.' : 'Žádné měřicí body. Přidej lednice, mrazáky nebo měřidla.'}</p>}
                {SKUPINY.map(g => {
                    const pol = seznam.filter(g.patri);
                    if (!pol.length) return null;
                    return (
                        <div key={g.id}>
                            <div className="flex justify-between" style={{ padding: '10px 14px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                                <span>{g.nazev}</span><span>{pol.length}</span>
                            </div>
                            {pol.map(b => radek(b, siroky))}
                        </div>
                    );
                })}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => {
        const b = detail?.bod;
        const lt = b && limitText(b);
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '6px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na měřicí body" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, margin: 0, color: 'var(--text-primary)' }}>{b?.nazev || '…'}</h2>
                            {b && <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>
                                {[TYP_BODU[b.typ], b.umisteni, CHOVANI[b.charakter], lt && `limit ${lt} ${b.jednotka || ''}`,
                                    b.frekvence === 'tydne' ? 'hlídá se každý týden' : b.frekvence === 'denne' ? 'hlídá se každý den' : null,
                                    b.kanal ? 'z uzlu pobočky' : b.cidlo_topic && 'čidlo'].filter(Boolean).join(' · ')}
                            </p>}
                        </div>
                        {b && !b.kanal && (
                            <button type="button" onClick={() => { const k = ted(); setZapis({ hodnota: '', kdy: k, puvodne: k, vysledek: null }); }}
                                className="inline-flex items-center gap-1.5 rounded-lg" style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: SECTION_COLOR, color: '#fff', border: 'none' }}>
                                <PenLine size={14} /> {isMobile ? 'Zapsat' : 'Zapsat měření'}
                            </button>
                        )}
                        {upravuje && b && (
                            <button type="button" onClick={() => setEditor({ id: b.id, ...b, instrukce_mereni: b.instrukce, aktualni_limit: detail.limity?.find(l => !l.do) ? { min_hodnota: detail.limity.find(l => !l.do).min, max_hodnota: detail.limity.find(l => !l.do).max } : null })}
                                className="inline-flex items-center gap-1.5 rounded-lg" style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                                <Pencil size={14} /> Upravit
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                    {!detail ? <p className="text-sm" style={{ color: muted }}>Načítám…</p> : (<>
                        {detail.rada.length > 1 && (
                            <div style={{ borderRadius: 10, padding: '10px 0', marginBottom: 12 }}>
                                <Trend rada={detail.rada} min={b.limit_min} max={b.limit_max} sirka={isMobile ? 320 : 560} vyska={110} />
                            </div>
                        )}
                        {b.kanal && (
                            <p className="text-xs" style={{ color: muted, margin: '0 0 12px' }}>
                                Z uzlu {b.kanal.uzel || ''}: {b.kanal.popis} · {AGREGACE[b.kanal_agregace] || 'průměr'} za {INTERVAL[b.kanal_interval_min] || `${b.kanal_interval_min} min`}
                                {b.kanal.hodnota != null && ` · teď ${cislo(b.kanal.hodnota)} ${b.jednotka || ''} (${kdy(b.kanal.kdy)})`}
                            </p>
                        )}
                        {!b.kanal && detail.pouziti.length > 0 && (
                            <p className="text-xs" style={{ color: muted, margin: '0 0 12px' }}>
                                Měří se v: {detail.pouziti.map(p => `${p.sablona}${p.povinne ? '' : ' (nepovinné)'}${p.opakovani ? ` · ${p.opakovani}` : ''}`).join(', ')}
                            </p>
                        )}
                        {!b.kanal && detail.pouziti.length === 0 && (
                            <p className="text-xs" style={{ color: '#F59E0B', margin: '0 0 12px' }}>Bod není v žádném checklistu — měří se jen tlačítkem Zapsat měření. Aby ho měřily směny, přidej ho jako položku do šablony.</p>
                        )}
                        <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '8px 0 6px' }}>Historie měření</p>
                        {detail.mereni.length === 0 && <p className="text-sm" style={{ color: muted }}>Zatím žádné měření.</p>}
                        {detail.mereni.map(m => {
                            const zle = m.stav === 'mimo' || m.podezrele;
                            return (
                                <div key={m.id} className="flex items-center" style={{ gap: 10, minHeight: 40, borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
                                    <span style={{ width: 110, color: muted, fontSize: 12 }}>{kdy(m.namereno_at)}</span>
                                    <span className="flex-1 truncate" style={{ color: muted, fontSize: 12 }}>
                                        {m.zdroj === 'cidlo' ? (b.kanal ? 'uzel' : 'čidlo') : m.zdroj === 'system' && !m.kdo ? 'systém' : m.kdo || '—'}
                                        {(m.opatreni || m.opatreni_text) && ` · ${[m.opatreni, m.opatreni_text].filter(Boolean).join(': ')}`}
                                        {m.vyrizeno_at && m.stav !== 'ok' && ' · vyřízeno'}
                                    </span>
                                    <b style={{ fontVariantNumeric: 'tabular-nums', color: zle ? '#EF4444' : m.stav === 'vynechano' ? '#F59E0B' : 'var(--text-primary)' }}>
                                        {m.stav === 'vynechano' ? 'nezměřeno' : `${cislo(m.hodnota)} ${m.jednotka || ''}`}
                                    </b>
                                </div>
                            );
                        })}
                        {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                    </>)}
                </div>
            </div>
        );
    };

    const panel = () => {
        const r = (l, h, barva) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: barva && h > 0 ? barva : 'var(--text-primary)' }}>{h}</b>
            </div>
        );
        const kd = vse.filter(b => b.stav !== 'ok');
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 6px' }}>Měřicí body</p>
                {r('Celkem', vse.length)}
                {r('S odchylkou', vse.filter(b => b.stav === 'odchylka').length, '#F87171')}
                {r('Nezměřené', vse.filter(b => b.stav === 'nezmereno').length, '#F59E0B')}
                {r('Automaticky (čidlo, uzel)', vse.filter(b => b.cidlo).length)}
                {uzly.length > 0 && (<>
                    <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' }}>Uzly poboček</p>
                    {uzly.map(u => (
                        <div key={u.id} className="flex items-center text-[13px]" style={{ gap: 8, minHeight: 30, borderBottom: `1px solid ${cara}` }}>
                            <span style={{ width: 8, height: 8, borderRadius: 99, flexShrink: 0, background: u.online ? '#10B981' : '#F59E0B' }} />
                            <span className="flex-1 truncate">{u.nazev}</span>
                            <span style={{ color: muted, fontSize: 12 }}>{u.online ? `${u.kanalu} kanálů` : (u.posledni_sync ? `naposledy ${kdy(u.posledni_sync)}` : 'zatím se neozval')}</span>
                        </div>
                    ))}
                </>)}
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' }}>K dořešení</p>
                {kd.length === 0 && <p className="text-xs" style={{ color: muted, margin: 0 }}>Všechno změřené a v pořádku.</p>}
                {kd.map(b => (
                    <button key={b.id} type="button" onClick={() => setVybranyId(b.id)} className="w-full text-left"
                        style={{ padding: '8px 10px', borderRadius: 9, fontSize: 12, cursor: 'pointer', marginBottom: 6, color: 'var(--text-primary)',
                            border: `1px solid ${b.stav === 'odchylka' ? 'rgba(239,68,68,0.45)' : 'rgba(245,158,11,0.45)'}`,
                            background: b.stav === 'odchylka' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)' }}>
                        {b.nazev} — {STAV[b.stav]?.[0]}
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
            {editor && (
                <BodEditor parametr={editor === 'new' ? null : editor} isDark={isDark} isMobile={isMobile} iStyle={inputStyle} mutedColor={muted} topOffset={top}
                    onClose={() => { setEditor(null); nacti(); nactiDetail(); }} />
            )}
            {zapis && detail?.bod && (() => {
                const b = detail.bod;
                const v = zapis.vysledek;
                const tlacitko = { height: 36, padding: '0 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' };
                return (
                    <ModalShell onClose={() => setZapis(null)} isDark={isDark} barva={SECTION_COLOR} ikona={PenLine} nadpis={`Zapsat: ${b.nazev}`} sirka={420}
                        paticka={(
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" onClick={() => setZapis(null)} style={{ ...tlacitko, background: 'transparent', color: muted }}>{v?.mereni ? 'Zavřít' : 'Zrušit'}</button>
                                {!v?.mereni && (
                                    <button type="button" onClick={ulozZapis} disabled={zapis.hodnota === ''} style={{ ...tlacitko, background: SECTION_COLOR, color: '#fff', opacity: zapis.hodnota === '' ? 0.5 : 1 }}>Uložit</button>
                                )}
                            </div>
                        )}>
                        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {v?.mereni ? (
                                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-primary)' }}>
                                    <b style={{ color: '#EF4444' }}>{cislo(v.mereni.hodnota)} {b.jednotka || ''}</b>{' '}
                                    {v.mereni.stav === 'mimo'
                                        ? `je mimo limit (${v.mereni.limit}). Uloženo jako odchylka — vedení dostalo upozornění a vyřídí ji v Přehledu.`
                                        : `nesedí na předchozí odečty${v.mereni.navrh != null ? ` — nechybí čárka? Spíš ${cislo(v.mereni.navrh)}.` : '.'} Uloženo, ale do spotřeby se nepočítá, dokud se neopraví.`}
                                </p>
                            ) : (<>
                                {b.instrukce && <p style={{ margin: 0, fontSize: 12.5, color: muted }}>{b.instrukce}</p>}
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: muted }}>
                                        Hodnota{b.jednotka ? ` (${b.jednotka})` : ''}{limitText(b) ? ` · limit ${limitText(b)}` : ''}
                                    </label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {muzeZaporne(b) && (
                                            <ZnamenkoTlacitko hodnota={zapis.hodnota} isDark={isDark} vyska={42}
                                                onZmena={n => setZapis(z => ({ ...z, hodnota: n }))} />
                                        )}
                                        <input autoFocus inputMode="decimal" value={zapis.hodnota} onChange={e => setZapis(z => ({ ...z, hodnota: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') ulozZapis(); }}
                                            style={{ ...inputStyle, height: 42, fontSize: 18, fontWeight: 700, flex: 1, minWidth: 0 }} placeholder={b.posledni ? `naposledy ${cislo(b.posledni.hodnota)}` : ''} />
                                    </div>
                                </div>
                                {detail.muze_zapsat_zpetne && (
                                    <div>
                                        <label style={{ fontSize: 11, fontWeight: 500, marginBottom: 4, display: 'block', color: muted }}>Kdy se měřilo</label>
                                        <input type="datetime-local" value={zapis.kdy} max={ted()} onChange={e => setZapis(z => ({ ...z, kdy: e.target.value }))} style={inputStyle} />
                                    </div>
                                )}
                                {v?.chyba && <p style={{ margin: 0, fontSize: 12, color: '#EF4444' }}>{v.chyba}</p>}
                            </>)}
                        </div>
                    </ModalShell>
                );
            })()}
        </div>
    );
}
